import React, { type JSX } from "react";

import { X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

import {
  TRANSLATION_BLOCK_KEYS,
  TRANSLATION_LOCALES,
  type TranslationBlock,
  type TranslationFaqItem,
  type TranslationLocale,
  type TranslationMap,
  availableLocales,
  dirtyLocales,
  emptyTranslation,
  isTranslationEmpty,
  localeOptionLabel,
  nonEmptyLocaleCount,
} from "./translations";

type TranslationBlockKey = (typeof TRANSLATION_BLOCK_KEYS)[number];

// ---------------------------------------------------------------------------
// The chip editor
// ---------------------------------------------------------------------------

export interface StringListEditorProps {
  value: string[];
  onChange: (next: string[]) => void;
  /** Names the input and its remove buttons, e.g. `"Greek"` or `"Keywords"`. */
  ariaLabel: string;
  disabled?: boolean;
}

/**
 * A `string[]` edited as chips: type, Enter to add, × to remove.
 *
 * The same interaction as the registered `tagList` AutoForm field, but with no
 * react-hook-form behind it — the enrichment page holds its draft in plain
 * state. Entries are a set: typing one that is already there is a no-op rather
 * than a duplicate chip with a colliding key.
 */
export function StringListEditor({
  value,
  onChange,
  ariaLabel,
  disabled,
}: StringListEditorProps) {
  const [draft, setDraft] = React.useState("");

  const add = () => {
    const next = draft.trim();
    setDraft("");
    if (!next || value.includes(next)) return;
    onChange([...value, next]);
  };

  return (
    <div className="flex flex-col gap-2">
      {value.length > 0 && (
        <div className="flex flex-wrap items-center gap-1">
          {value.map((item) => (
            <span
              key={item}
              className="inline-flex items-center gap-1 rounded-md bg-secondary px-2 py-1 text-sm text-secondary-foreground"
            >
              <span>{item}</span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-5 hover:bg-transparent"
                aria-label={`Remove ${ariaLabel} ${item}`}
                disabled={disabled}
                onClick={() =>
                  onChange(value.filter((entry) => entry !== item))
                }
              >
                <X className="size-3" />
              </Button>
            </span>
          ))}
        </div>
      )}
      <Input
        aria-label={ariaLabel}
        value={draft}
        disabled={disabled}
        placeholder="Type and press Enter to add"
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key !== "Enter") return;
          // Enter inside a form would submit it; this editor is stand-alone.
          event.preventDefault();
          add();
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// The section
// ---------------------------------------------------------------------------

export interface TranslationsSectionProps {
  value: TranslationMap;
  stored: TranslationMap;
  onChange: (next: TranslationMap) => void;
  disabled?: boolean;
}

/** One labelled form row. */
function Field({
  id,
  label,
  children,
}: {
  id: string;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium">
        {label}
      </label>
      {children}
    </div>
  );
}

/** Whether one member of a locale block carries something a curator typed. */
function fieldHasContent(
  value: TranslationBlock[TranslationBlockKey],
): boolean {
  if (Array.isArray(value)) return value.length > 0;
  return typeof value === "string" && value.trim().length > 0;
}

interface LocaleEditorProps {
  locale: TranslationLocale;
  block: TranslationBlock;
  disabled?: boolean;
  onChange: (next: TranslationBlock) => void;
}

/**
 * The editor for one locale block, with the seven members in DTO order.
 *
 * Only the members that carry something are shown, so a locale holding one
 * paragraph reads as one paragraph (MOO-4803 owner feedback). The rest are one
 * click away. The parent keys this by locale, so the click and the "revealed"
 * set last exactly as long as the curator stays on the locale.
 */
function LocaleEditor({
  locale,
  block,
  disabled,
  onChange,
}: LocaleEditorProps): JSX.Element {
  // Members pulled into view that the block does not hold: either the curator
  // asked for them, or the curator just emptied them and must keep the cursor.
  const [revealed, setRevealed] = React.useState<TranslationBlockKey[]>([]);

  const emptyBlock = isTranslationEmpty(block);
  const emptyKeys = TRANSLATION_BLOCK_KEYS.filter(
    (key) => !fieldHasContent(block[key]),
  );
  // A locale that was just added holds nothing: it shows all seven fields and
  // offers nothing to reveal.
  const hiddenKeys = emptyBlock
    ? []
    : emptyKeys.filter((key) => !revealed.includes(key));
  const shownKeys = emptyBlock
    ? TRANSLATION_BLOCK_KEYS
    : TRANSLATION_BLOCK_KEYS.filter((key) => !hiddenKeys.includes(key));
  const shows = (key: TranslationBlockKey) => shownKeys.includes(key);

  const faq: TranslationFaqItem[] = Array.isArray(block.faq) ? block.faq : [];

  const setField = (key: TranslationBlockKey, next: unknown) => {
    const updated = { ...block, [key]: next } as TranslationBlock;
    // A field the curator just emptied must not vanish under the cursor.
    if (!fieldHasContent(updated[key])) {
      setRevealed((prev) => (prev.includes(key) ? prev : [...prev, key]));
    }
    onChange(updated);
  };

  const toggle = () =>
    setRevealed(hiddenKeys.length > 0 ? [...revealed, ...hiddenKeys] : []);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">{localeOptionLabel(locale)}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {shows("seoTitle") && (
          <Field id={`${locale}-seoTitle`} label="SEO title">
            <Input
              id={`${locale}-seoTitle`}
              value={block.seoTitle ?? ""}
              disabled={disabled}
              onChange={(event) => setField("seoTitle", event.target.value)}
            />
          </Field>
        )}

        {shows("shortDescription") && (
          <Field id={`${locale}-shortDescription`} label="Short description">
            <Textarea
              id={`${locale}-shortDescription`}
              value={block.shortDescription ?? ""}
              disabled={disabled}
              onChange={(event) =>
                setField("shortDescription", event.target.value)
              }
            />
          </Field>
        )}

        {shows("about") && (
          <Field id={`${locale}-about`} label="About">
            <Textarea
              id={`${locale}-about`}
              value={block.about ?? ""}
              disabled={disabled}
              onChange={(event) => setField("about", event.target.value)}
            />
          </Field>
        )}

        {shows("overview") && (
          <Field id={`${locale}-overview`} label="Overview">
            <Textarea
              id={`${locale}-overview`}
              value={block.overview ?? ""}
              disabled={disabled}
              onChange={(event) => setField("overview", event.target.value)}
            />
          </Field>
        )}

        {shows("faq") && (
          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium">FAQ</span>
            {faq.map((row, index) => (
              <div
                key={index}
                className="flex flex-col gap-2 rounded-md border p-3"
              >
                <Field
                  id={`${locale}-faq-${index}-question`}
                  label={`Question ${index + 1}`}
                >
                  <Input
                    id={`${locale}-faq-${index}-question`}
                    value={row.question ?? ""}
                    disabled={disabled}
                    onChange={(event) =>
                      setField(
                        "faq",
                        faq.map((entry, at) =>
                          at === index
                            ? { ...entry, question: event.target.value }
                            : entry,
                        ),
                      )
                    }
                  />
                </Field>
                <Field
                  id={`${locale}-faq-${index}-answer`}
                  label={`Answer ${index + 1}`}
                >
                  <Textarea
                    id={`${locale}-faq-${index}-answer`}
                    value={row.answer ?? ""}
                    disabled={disabled}
                    onChange={(event) =>
                      setField(
                        "faq",
                        faq.map((entry, at) =>
                          at === index
                            ? { ...entry, answer: event.target.value }
                            : entry,
                        ),
                      )
                    }
                  />
                </Field>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="self-start"
                  disabled={disabled}
                  onClick={() =>
                    setField(
                      "faq",
                      faq.filter((_, at) => at !== index),
                    )
                  }
                >
                  Remove FAQ row
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="self-start"
              disabled={disabled}
              onClick={() =>
                setField("faq", [...faq, { question: "", answer: "" }])
              }
            >
              Add FAQ row
            </Button>
          </div>
        )}

        {shows("keywords") && (
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium">Keywords</span>
            <StringListEditor
              value={block.keywords ?? []}
              ariaLabel="Keywords"
              disabled={disabled}
              onChange={(next) => setField("keywords", next)}
            />
          </div>
        )}

        {shows("relatedSearches") && (
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium">Related searches</span>
            <StringListEditor
              value={block.relatedSearches ?? []}
              ariaLabel="Related searches"
              disabled={disabled}
              onChange={(next) => setField("relatedSearches", next)}
            />
          </div>
        )}

        {!emptyBlock && (hiddenKeys.length > 0 || revealed.length > 0) && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="self-start"
            disabled={disabled}
            onClick={toggle}
          >
            {hiddenKeys.length > 0
              ? `Show ${hiddenKeys.length} empty fields`
              : "Hide empty fields"}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * The translations map editor: a chip per locale in the draft, a picker for the
 * ones still available, and the card for the selected locale.
 *
 * Every edit rebuilds the whole map, so the parent sees a new object and a
 * `useMemo` over it re-runs. There is deliberately no delete affordance: the
 * API has no delete (design B.2), and a blanked locale is written as `{}`.
 */
export function TranslationsSection({
  value,
  stored,
  onChange,
  disabled,
}: TranslationsSectionProps): JSX.Element {
  const [selected, setSelected] = React.useState<TranslationLocale | null>(
    null,
  );

  const inDraft = TRANSLATION_LOCALES.filter(
    (locale) => value?.[locale] !== undefined,
  );
  // A locale that leaves the draft (it cannot today) must not leave a blank
  // editor behind, so the selection falls back to the first chip.
  const current =
    selected && value?.[selected] !== undefined
      ? selected
      : (inDraft[0] ?? null);
  const block: TranslationBlock | null = current
    ? (value[current] ?? {})
    : null;

  // A locale is "saved" when something is stored for it and the draft would not
  // be written. Reusing the diff keeps the marker honest about what save does.
  const dirty = React.useMemo(
    () => new Set(dirtyLocales(stored, value)),
    [stored, value],
  );
  const isSaved = (locale: TranslationLocale) =>
    stored?.[locale] !== undefined && !dirty.has(locale);

  const patch = (locale: TranslationLocale, next: TranslationBlock) =>
    onChange({ ...value, [locale]: next });

  const addLocale = (locale: TranslationLocale) => {
    setSelected(locale);
    patch(locale, emptyTranslation());
  };

  const options = availableLocales(value);

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-baseline gap-2">
          <h3 className="text-lg font-semibold text-foreground">
            Translations
          </h3>
          <span className="text-sm text-muted-foreground">
            {`${nonEmptyLocaleCount(value)} of ${TRANSLATION_LOCALES.length} locales have content`}
          </span>
        </div>
        <Select
          value=""
          disabled={disabled || options.length === 0}
          onValueChange={(next) => {
            if (!TRANSLATION_LOCALES.includes(next as TranslationLocale))
              return;
            addLocale(next as TranslationLocale);
          }}
        >
          <SelectTrigger className="w-52" aria-label="Add locale">
            <SelectValue placeholder="Add locale" />
          </SelectTrigger>
          <SelectContent>
            {options.map((locale) => (
              <SelectItem key={locale} value={locale}>
                {localeOptionLabel(locale)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {inDraft.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          {inDraft.map((locale) => {
            const empty = isTranslationEmpty(value[locale]);
            const saved = isSaved(locale);
            return (
              <button
                key={locale}
                type="button"
                aria-pressed={current === locale}
                onClick={() => setSelected(locale)}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm",
                  current === locale
                    ? "border-primary bg-primary text-primary-foreground"
                    : "bg-secondary text-secondary-foreground",
                )}
              >
                <span>{locale}</span>
                {!empty && (
                  <span
                    aria-hidden
                    className="size-1.5 rounded-full bg-emerald-500"
                  />
                )}
                <span
                  className="text-xs opacity-80"
                  data-testid={`locale-status-${locale}`}
                >
                  {saved ? "saved" : "unsaved"}
                </span>
              </button>
            );
          })}
        </div>
      )}

      <p className="text-sm text-muted-foreground">
        Locales cannot be deleted. To blank a locale, clear its fields and save.
      </p>

      {current && block && (
        <LocaleEditor
          key={current}
          locale={current}
          block={block}
          disabled={disabled}
          onChange={(next) => patch(current, next)}
        />
      )}
    </section>
  );
}
